import {
  CalendarDetailsVisibility,
  CalendarTaskSync,
  ExternalConnection,
  LinkedItemGoal,
  Task,
  TaskCalendarOverride,
} from '@griply/types';
import { getCalendarSyncStates } from '@griply/utils';
import { useCallback, useMemo } from 'react';

export type UseCalendarTaskSyncOptions = {
  connections: ExternalConnection[];
  goals: LinkedItemGoal[];
  setTaskOverride: (task: Task, patch: Partial<TaskCalendarOverride>) => void;
};

export const useCalendarTaskSync = (
  tasks: Task[],
  { connections, goals, setTaskOverride }: UseCalendarTaskSyncOptions,
): CalendarTaskSync => {
  const states = useMemo(
    () => getCalendarSyncStates({ tasks, connections, goals }),
    [tasks, connections, goals],
  );

  const onToggleVisibility = useCallback(
    (task: Task, isHidden: boolean) =>
      setTaskOverride(task, {
        detailsVisibility: isHidden
          ? CalendarDetailsVisibility.Visible
          : CalendarDetailsVisibility.Hidden,
      }),
    [setTaskOverride],
  );

  const onToggleSync = useCallback(
    (task: Task, isSynced: boolean) =>
      setTaskOverride(task, { synced: !isSynced }),
    [setTaskOverride],
  );

  return useMemo(
    () => ({ states, onToggleVisibility, onToggleSync }),
    [states, onToggleVisibility, onToggleSync],
  );
};
